import { ApiService } from '../apiService'
import {
  FlowVersionDto,
  FlowVersionSummaryDto,
  SaveFlowRequest,
  CompileResultDto,
  WorkflowValidationResultDto,
  AudioMetadata,
} from '@features/adventures/types/workflow.types'

// Audio upload response from the API
export interface AudioUploadResultDto {
  mediaFileId: string
  fileName: string
  mimeType: string
  fileSize: number
  durationSeconds: number
  audioUrl: string
  metadata: AudioMetadata
}

// Image upload response from the API
export interface ImageUploadResultDto {
  mediaFileId: string
  fileName: string
  mimeType: string
  fileSize: number
  imageUrl: string
  width: number
  height: number
}

const BASE_URL = '/api/adventures'

export const workflowDesignerApi = {
  // ====================
  // Flow Version Operations (New Architecture)
  // ====================

  // Get current/latest flow version
  getFlow: (adventureId: string, version?: number) =>
    ApiService.get<FlowVersionDto>(
      `${BASE_URL}/${adventureId}/flow${version !== undefined ? `/${version}` : ''}`
    ),

  // Save flow version (PUT overwrites draft, creates new version if compiled)
  saveFlow: (adventureId: string, request: SaveFlowRequest) =>
    ApiService.put<FlowVersionDto>(`${BASE_URL}/${adventureId}/flow`, request),

  // Get version history (metadata only)
  getFlowHistory: (adventureId: string) =>
    ApiService.get<FlowVersionSummaryDto[]>(`${BASE_URL}/${adventureId}/flow/history`),

  // Delete draft version (only if not compiled)
  deleteFlowVersion: (adventureId: string, version: number) =>
    ApiService.delete<void>(`${BASE_URL}/${adventureId}/flow/${version}`),

  // ====================
  // Lifecycle Operations
  // ====================

  // Compile JSON to relational tables
  compileFlow: (adventureId: string) =>
    ApiService.post<CompileResultDto>(`${BASE_URL}/${adventureId}/flow/compile`),

  // Start testing (requires compiled)
  startTesting: (adventureId: string) =>
    ApiService.post<void>(`${BASE_URL}/${adventureId}/flow/start-testing`),

  // Publish (requires testing status)
  publishFlow: (adventureId: string) =>
    ApiService.post<void>(`${BASE_URL}/${adventureId}/flow/publish`),

  // ====================
  // Validation Operations
  // ====================

  validateWorkflow: (adventureId: string) =>
    ApiService.post<WorkflowValidationResultDto>(
      `${BASE_URL}/${adventureId}/flow/validate`
    ),

  hasCycles: (adventureId: string) =>
    ApiService.get<boolean>(`${BASE_URL}/${adventureId}/flow/has-cycles`),

  // Audio Operations
  uploadNodeAudio: (adventureId: string, nodeId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<AudioUploadResultDto>(
      `${BASE_URL}/${adventureId}/flow/nodes/${nodeId}/audio`,
      formData
    )
  },

  deleteNodeAudio: (adventureId: string, nodeId: string) =>
    ApiService.delete<void>(
      `${BASE_URL}/${adventureId}/flow/nodes/${nodeId}/audio`
    ),

  // Image Operations
  uploadNodeImage: (adventureId: string, nodeId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<ImageUploadResultDto>(
      `${BASE_URL}/${adventureId}/node/${nodeId}/image`,
      formData
    )
  },

  deleteNodeImage: (adventureId: string, nodeId: string) =>
    ApiService.delete<void>(
      `${BASE_URL}/${adventureId}/node/${nodeId}/image`
    ),

  // Route Operations
  generateEdgeRoute: (
    adventureId: string,
    edgeId: string,
    request: {
      sourceLatitude: number
      sourceLongitude: number
      targetLatitude: number
      targetLongitude: number
      travelMode: string
    }
  ) =>
    ApiService.post<{
      coordinates: [number, number][]
      distanceMeters: number
      durationSeconds: number
      cachedAt: string
      expiresAt: string
    }>(`${BASE_URL}/${adventureId}/flow/edges/${edgeId}/route`, request),

  generateRoute: async (
    adventureId: string,
    request: {
      sourceLatitude: number
      sourceLongitude: number
      targetLatitude: number
      targetLongitude: number
      travelMode: string
    }
  ): Promise<{
    coordinates: [number, number][]
    distanceMeters: number
    durationSeconds: number
  }> => {
    const response = await ApiService.post<{
      success: boolean
      route: {
        coordinates: [number, number][]
        distanceMeters: number
        durationSeconds: number
        travelMode: string
        cachedAt: string | null
        expiresAt: string | null
      }
    }>(`${BASE_URL}/${adventureId}/flow/generate-route`, request)
    return response.route
  },

  clearEdgeRoute: (adventureId: string, edgeId: string) =>
    ApiService.delete<void>(`${BASE_URL}/${adventureId}/flow/edges/${edgeId}/route`),
}
